'use client';

import { Fragment, SyntheticEvent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { NewInventoryFiltersContext } from '@/contexts/NewInventoryFiltersContext';
import {
  BANNER_INLINE_ELEMENTS_COUNT,
  ITEMS_PER_PAGE_MOBILE,
  ITEMS_PER_PAGE_TABLET,
  ITEMS_BETWEEN_FILLER_CARDS,
  ITEMS_TO_SKIP_BEFORE_FILLER_CARD,
} from '@/constants/inventory';
import { getItems } from '@/lib/actions';
import { convertFilterSection, convertSortBy, convertTagFilter, isFilterSection } from '@/tools/inventory';
import { isMobile, isTablet } from '@/tools/views';

import { Grid, IGridInlineElement, LaMesaRecVanLogo, Pagination } from '@lamesarv-sdk/components';
import { MobileTabletFilters } from '@/components/Inventory/InventoryFilters/MobileTabletFilters';
import { inventoryGridClassName } from '@/components/Inventory/InventoryGrid';
import { InventoryItem } from '@/components/Inventory/InventoryItem';
import { ResultsEmpty } from './ResultsEmpty';

import { IBasicInventory, IMetaData, InventoryField, PropsWithClasses } from '@lamesarv-sdk/types';
import { SortBy } from '@/types/inventory';
import { IDynamicsOptionSet } from '@/types/dynamicsForms';
import { IComponentSearchResults } from '@/types/cms';
import { ResultsBanner } from './ResultsBanner';
import { ResultsFillerCard } from './ResultsFillerCard';

const RefineSortBy = (props: PropsWithClasses) => {
  const { sortBy, setSortBy } = useContext(NewInventoryFiltersContext);

  const items = [
    { label: SortBy.RECOMMENDED, value: SortBy.RECOMMENDED, classes: 'w-36' },
    { label: SortBy.NEWEST, value: SortBy.NEWEST, classes: 'w-24' },
    { label: SortBy.OLDEST, value: SortBy.OLDEST, classes: 'w-24' },
    { label: SortBy.PRICE_LOW_TO_HIGH, value: SortBy.PRICE_LOW_TO_HIGH, classes: 'w-42' },
    { label: SortBy.PRICE_HIGH_TO_LOW, value: SortBy.PRICE_HIGH_TO_LOW, classes: 'w-42' },
  ];

  const handleChange = (e: SyntheticEvent) => {
    const value = (e.target as HTMLInputElement).value;

    setSortBy(value as SortBy);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sortByClasses = items.find((item) => item.value === sortBy)?.classes || 'w-24';

  return (
    <div className="flex flex-row items-center gap-2">
      <label htmlFor="select-sort-by" className="text-md-regular text-black-500">
        Sort By:
      </label>
      <select
        id="select-sort-by"
        className={twMerge(
          'p-0 pr-8 border-none text-md-regular text-black-350 focus:outline-none focus:border-none focus:ring-0',
          sortByClasses,
          props.className,
        )}
        value={sortBy}
        onChange={handleChange}
        data-testid="select-sort-by"
      >
        {items.map((option, index) => {
          return (
            <option key={option.value + index} value={option.value}>
              {option.label}
            </option>
          );
        })}
      </select>
    </div>
  );
};

const DESKTOP_PER_PAGE_OPTIONS = [15, 30, 45];

const getItemsBetweenFillerCards = (fillerCardsAmount: number) => {
  const itemsBetweenFillerCards = isMobile()
    ? ITEMS_BETWEEN_FILLER_CARDS.MOBILE
    : isTablet()
      ? ITEMS_BETWEEN_FILLER_CARDS.TABLET
      : ITEMS_BETWEEN_FILLER_CARDS.DESKTOP;

  if (fillerCardsAmount === 1) return 2 * itemsBetweenFillerCards + 1;

  return itemsBetweenFillerCards;
};

const getPerPageWithoutFillerCards = (amount: number, fillerCardsAmount: number) => {
  let fillerCardsToAdd = 0;
  if (fillerCardsAmount > 0) {
    const itemsAfterSkip = Math.max(0, amount - ITEMS_TO_SKIP_BEFORE_FILLER_CARD);
    const itemsBetweenFillerCards = getItemsBetweenFillerCards(fillerCardsAmount) + 1;

    fillerCardsToAdd = Math.ceil(itemsAfterSkip / itemsBetweenFillerCards);
  }

  const finalAmount = amount - fillerCardsToAdd;

  return finalAmount;
};

interface IRefinePaginationProps extends PropsWithClasses {
  nbHits: number;
  fillerCardsAmount: number;
  callback?: (page: number) => void;
}

const RefinePagination = (props: IRefinePaginationProps) => {
  const { page, setPage, perPage } = useContext(NewInventoryFiltersContext);

  const callback = (currentPage: number) => {
    setPage(currentPage);

    if (props.callback) {
      props.callback(currentPage);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const customLegend = (amountFrom: number, amountTo: number, totalItems: number) => {
    let legendText = '';

    if (totalItems === 0) {
      legendText = 'No results found';
    }
    if (totalItems === 1) {
      legendText = '1 result found';
    }

    legendText = `Showing ${amountFrom} - ${amountTo} of ${totalItems}`;

    return <Fragment>{legendText}</Fragment>;
  };

  const classesBase = 'text-[16px] px-0 w-[40px] rounded-[4px]';
  const classesItemsNavigation = 'flex flex-row w-auto px-2';
  const classesNormal = 'bg-white border-black-200 text-black-500';
  const classesDisabled = 'bg-white border-black-100 text-black-200 cursor-not-allowed';
  const classesSelected = 'bg-black border-black text-white';

  return (
    <Pagination
      totalItems={props.nbHits}
      pageSize={perPage ?? getPerPageWithoutFillerCards(DESKTOP_PER_PAGE_OPTIONS[0], props.fillerCardsAmount)}
      radix={0}
      offsetStart={3}
      offsetEnd={3}
      callback={callback}
      customLabelNext="Next"
      customLabelPrevious="Back"
      displayAlwaysNavigation={true}
      className={props.className}
      currentPage={page ?? 1}
      classesItemsPrevious={classesItemsNavigation}
      classesItemsNext={classesItemsNavigation}
      classesLegend="text-[16px] text-black-350"
      classesBase={classesBase}
      classesNormal={classesNormal}
      classesDisabled={classesDisabled}
      classesSelected={classesSelected}
      customLegend={customLegend}
    />
  );
};

interface ISearchResultsProps extends IComponentSearchResults {
  locationsOptionSet?: IDynamicsOptionSet;
  metadata: IMetaData;
}

export const SearchResults = ({
  banner_data_tag,
  banner_data_tag_group,
  banner_top_disable_pause_on_hover,
  banner_top_display_arrows,
  banner_top_display_dots,
  banner_top_images,
  banner_top_classes,
  banner_inline_disable_pause_on_hover,
  banner_inline_display_arrows,
  banner_inline_display_dots,
  banner_inline_images,
  banner_inline_classes,
  banner_speed,
  locationsOptionSet,
  metadata,
  filler_cards: fillerCards = [],
}: ISearchResultsProps) => {
  const {
    filters,
    baseFilters,
    sortBy,
    query,
    perPage: perPageDesktop,
    page,
    tagFilters,
  } = useContext(NewInventoryFiltersContext);

  const [items, setItems] = useState<IBasicInventory[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const currentMobilePageRef = useRef<number>(1);

  const _getItems = useCallback(
    (currentPage?: number) => {
      const filtersArr = [convertFilterSection(filters, baseFilters, tagFilters)];
      for (const section of Object.keys(tagFilters)) {
        if (isFilterSection(section)) continue;

        filtersArr.push(convertTagFilter(section, tagFilters));
      }

      const sort = convertSortBy(sortBy);

      let perPage = perPageDesktop ?? getPerPageWithoutFillerCards(DESKTOP_PER_PAGE_OPTIONS[0], fillerCards.length);
      if (isMobile()) {
        perPage = getPerPageWithoutFillerCards(ITEMS_PER_PAGE_MOBILE, fillerCards.length);
      } else if (isTablet()) {
        perPage = getPerPageWithoutFillerCards(ITEMS_PER_PAGE_TABLET, fillerCards.length);
      }

      return getItems({
        filters: filtersArr.filter(Boolean).join(' && '),
        sort,
        currentPage,
        q: query,
        perPage,
      });
    },
    [filters, baseFilters, sortBy, query, perPageDesktop, tagFilters, fillerCards.length],
  );

  const getMoreItems = async () => {
    currentMobilePageRef.current++;

    setIsLoading(true);

    try {
      const itemsResult = await _getItems(currentMobilePageRef.current);

      setItems((prevItems) => [...prevItems, ...itemsResult.items]);
      setTotal(itemsResult.total);
    } catch (error) {
      console.error('Error fetching more items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPageItems = (page: number, controller?: AbortController) => {
    setItems([]);
    setTotal(null);
    setIsLoading(true);

    _getItems(page)
      .then((result) => {
        if (controller?.signal?.aborted) return;

        setItems(result.items);
        setTotal(result.total);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const hasFillerCards = fillerCards.length > 0;

  const inlineItems: IGridInlineElement[] = [
    {
      getItem: () => {
        return (
          <ResultsBanner
            classes={twMerge('hidden lg:block lg:col-span-2 xl:col-span-3', banner_inline_classes)}
            customSessionKey="searchResultsInlineBanner"
            data_tag={banner_data_tag}
            data_tag_group={banner_data_tag_group}
            disable_pause_on_hover={banner_inline_disable_pause_on_hover}
            display_arrows={banner_inline_display_arrows}
            display_dots={banner_inline_display_dots}
            images={banner_inline_images}
            speed={banner_speed}
          />
        );
      },
      afterItemsNumber: BANNER_INLINE_ELEMENTS_COUNT - (hasFillerCards ? 1 : 0),
      isUnique: true,
    },
  ];

  if (hasFillerCards) {
    let count = -ITEMS_TO_SKIP_BEFORE_FILLER_CARD;
    let fillerCardCount = 0;

    inlineItems.push({
      afterItemsNumber: 1,
      getItem: () => {
        const itemsBetweenFillerCards = getItemsBetweenFillerCards(fillerCards.length);

        count++;
        const isFiller = count % itemsBetweenFillerCards === 0;

        if (!isFiller) return null;

        const fillerCard = fillerCards[fillerCardCount % fillerCards.length];

        if (!fillerCard) return null;

        fillerCardCount++;

        return (
          <div className="size-full px-3 xs:px-4 md:px-2.5 pb-4">
            <ResultsFillerCard item={fillerCard} />
          </div>
        );
      },
    });
  }

  useEffect(() => {
    const controller = new AbortController();

    getPageItems(page, controller);

    return () => {
      controller.abort();
    };
  }, [_getItems, page]);

  return (
    <Fragment>
      <MobileTabletFilters total={total} />
      {items.length > 0 && (
        <div className="flex flex-col items-end gap-10 max-lg:py-10">
          <div className="hidden lg:flex flex-col gap-4 md:flex-row items-start md:items-center justify-between w-full pl-6 md:pl-2.5">
            <div className="flex flex-row items-center gap-2">
              <span className="text-md-medium" data-testid="total-results">
                {total}
              </span>
              <span className="text-md-regular text-black-350">results</span>
            </div>
            <div className="flex flex-row items-center justify-between gap-4 w-full md:w-auto">
              <RefineSortBy />
            </div>
          </div>
          <ResultsBanner
            classes={twMerge('', banner_top_classes)}
            customSessionKey="searchResultsTopBanner"
            data_tag={banner_data_tag}
            data_tag_group={banner_data_tag_group}
            disable_pause_on_hover={banner_top_disable_pause_on_hover}
            display_arrows={banner_top_display_arrows}
            display_dots={banner_top_display_dots}
            images={banner_top_images}
            speed={banner_speed}
          />
          <Grid
            items={items.map((item) => (
              <InventoryItem key={item[InventoryField.id]} item={item} />
            ))}
            itemsInline={inlineItems}
            baseCols={1}
            smCols={1}
            mdCols={2}
            lgCols={2}
            xlCols={3}
            gap={6}
            className={twMerge(inventoryGridClassName, 'lg:gap-x-0 xl:gap-x-2 w-full')}
          />
          <div className="hidden lg:block">
            <RefinePagination
              nbHits={total}
              fillerCardsAmount={fillerCards.length}
              className="font-helvetica items-center px-4"
            />
          </div>
        </div>
      )}
      {isLoading && !total && (
        <div className="flex flex-row justify-center mt-16 lg:mt-32 animate-pulse">
          <LaMesaRecVanLogo className="h-20" />
        </div>
      )}
      {total === 0 && !isLoading && <ResultsEmpty metadata={metadata} locationsOptionSet={locationsOptionSet} />}
      {!!total && total > items.length && (
        <div className="flex justify-center lg:hidden">
          <button
            type="button"
            onClick={getMoreItems}
            disabled={isLoading}
            className="border border-blue-500 rounded bg-white text-blue-500 text-sm font-medium px-6 py-3 flex items-center justify-center enabled:hover:bg-blue-50 disabled:opacity-50 disabled:cursor-wait"
          >
            Load More
          </button>
        </div>
      )}
    </Fragment>
  );
};
